import React from "react";
import Axios from "axios";
import { DEFAULT_PER_PAGE } from "../../../../helpers/default";
import ContentBlock from "../../../../components/Layout/ContentBlock";
import FilterMenu from "../../../../components/Layout/FilterMenu";
import TabUi from "../TabUi";
import { useModal } from "../../../../hooks";
import { useMutation, useQuery, useQueryClient } from "react-query";
import TableFilterBox from "../../../../components/common/TableFilterBox";
import ContentValidation from "../../../../components/common/ContentValidation";
import { ButtonDelete, ButtonEditRow, ButtonHistoryRow } from "../../../../components/common/Buttons";
import {
  ButtonSecondary,
  Pagination,
  SelectInput,
  TextInput,
} from "../../../../components/globals";
import { Modal } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import {
  antdAsyncMessageError,
  antdAsyncMessageLoading,
  antdAsyncMessageSuccess,
} from "../../../../helpers/AsyncMessage";
import ModalNonPublishableKeyword from "./Modals/ModalNonPublishableKeyword";
import ModalNonPublishableKeywordHistory from "./Modals/ModalNonPublishableKeywordHistory";

const { confirm } = Modal;

const SCOPE_OPTIONS = [
  { label: "Bigbox_lt", value: "Bigbox_lt" },
  { label: "Bigbox_lv", value: "Bigbox_lv" },
  { label: "Bigbox_ee", value: "Bigbox_ee" },
  { label: "Bigbox_fi", value: "Bigbox_fi" },
  { label: "Technorama_lt", value: "Technorama_lt" },
];

const NonPublishableKeywords = ({ tabId, setTabId }) => {
  const [totals, setTotals] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const [perPage, setPerPage] = React.useState(DEFAULT_PER_PAGE);
  const [keywordFilter, setKeywordFilter] = React.useState("");
  const [queryKeyword, setQueryKeyword] = React.useState("");
  const [activeFilter, setActiveFilter] = React.useState("");
  const [queryActive, setQueryActive] = React.useState("");
  const [typeFilter, setTypeFilter] = React.useState("");
  const [queryType, setQueryType] = React.useState("");
  const [scopeFilter, setScopeFilter] = React.useState([]);
  const [queryScope, setQueryScope] = React.useState([]);
  const [categoryTitleFilter, setCategoryTitleFilter] = React.useState("");
  const [queryCategoryTitle, setQueryCategoryTitle] = React.useState("");
  const [brandCodeFilter, setBrandCodeFilter] = React.useState("");
  const [queryBrandCode, setQueryBrandCode] = React.useState("");
  const [disableByScopeFilter, setDisableByScopeFilter] = React.useState("");
  const [queryDisableByScope, setQueryDisableByScope] = React.useState("");
  const [ignoreIfWarehouseStockFilter, setIgnoreIfWarehouseStockFilter] = React.useState("");
  const [queryIgnoreIfWarehouseStock, setQueryIgnoreIfWarehouseStock] = React.useState("");

  const [expandedCategoryRows, setExpandedCategoryRows] = React.useState({});
  const [historyRecordId, setHistoryRecordId] = React.useState(null);

  const queryClient = useQueryClient();
  const { modalOpt, setModalOpt, closeModal } = useModal();

  const fetchKeywords = async () => {
    let url = `/api/v1/non_publishable_keywords?page=${page}&per_page=${perPage}`;

    if (queryKeyword) {
      url += `&q[keywords_cont]=${encodeURIComponent(queryKeyword)}`;
    }

    if (queryActive !== "") {
      url += `&q[active_eq]=${queryActive}`;
    }

    if (queryType) {
      url += `&q[keyword_match_type_eq]=${encodeURIComponent(queryType)}`;
    }

    if (queryScope.length) {
      queryScope.forEach((scope) => {
        url += `&q[scope_in][]=${encodeURIComponent(scope)}`;
      });
    }

    if (queryCategoryTitle) {
      url += `&q[category_title_cont]=${encodeURIComponent(queryCategoryTitle)}`;
    }

    if (queryBrandCode) {
      url += `&q[brand_code_cont]=${encodeURIComponent(queryBrandCode)}`;
    }

    if (queryDisableByScope !== "") {
      url += `&q[disable_by_scope_eq]=${queryDisableByScope}`;
    }

    if (queryIgnoreIfWarehouseStock !== "") {
      url += `&q[ignore_if_warehouse_stock_eq]=${queryIgnoreIfWarehouseStock}`;
    }

    const response = await Axios.get(url);
    return response.data;
  };

  const {
    data: keywordsResponse,
    isLoading,
    error,
    refetch,
  } = useQuery(
    [
      "nonPublishableKeywords",
      page,
      perPage,
      queryKeyword,
      queryActive,
      queryType,
      queryScope,
      queryCategoryTitle,
      queryBrandCode,
      queryDisableByScope,
      queryIgnoreIfWarehouseStock,
    ],
    fetchKeywords,
    {
      keepPreviousData: true,
      onSuccess: (response) => {
        setTotals(response?.totals || 0);
      },
    }
  );

  const removeMutation = useMutation({
    mutationFn: ({ id }) => {
      antdAsyncMessageLoading({
        key: "remove_non_publishable_keyword",
        message: "Removing keyword",
      });
      return Axios.delete(`/api/v1/non_publishable_keywords/${id}`);
    },
    onSuccess: () => {
      antdAsyncMessageSuccess({
        key: "remove_non_publishable_keyword",
        message: "Successfully removed",
      });
      queryClient.invalidateQueries("nonPublishableKeywords");
    },
    onError: antdAsyncMessageError,
  });

  const onFilter = () => {
    setPage(1);
    setQueryKeyword(keywordFilter.trim());
    setQueryActive(activeFilter);
    setQueryType(typeFilter);
    setQueryScope(scopeFilter);
    setQueryCategoryTitle(categoryTitleFilter.trim());
    setQueryBrandCode(brandCodeFilter.trim());
    setQueryDisableByScope(disableByScopeFilter);
    setQueryIgnoreIfWarehouseStock(ignoreIfWarehouseStockFilter);
  };

  const clearFilter = () => {
    setKeywordFilter("");
    setActiveFilter("");
    setTypeFilter("");
    setScopeFilter([]);
    setCategoryTitleFilter("");
    setBrandCodeFilter("");
    setQueryKeyword("");
    setQueryActive("");
    setQueryType("");
    setQueryScope([]);
    setQueryCategoryTitle("");
    setQueryBrandCode("");
    setDisableByScopeFilter("");
    setQueryDisableByScope("");
    setIgnoreIfWarehouseStockFilter("");
    setQueryIgnoreIfWarehouseStock("");
    setPage(1);
  };

  const formatLimitedCodes = (codes = [], limit = 10) => {
    if (!Array.isArray(codes) || codes.length === 0) {
      return "- (Total: 0)";
    }

    const limited = codes.slice(0, limit).join(", ");
    const suffix = codes.length > limit ? " ..." : "";

    return `${limited}${suffix} (Total: ${codes.length})`;
  };

  const toggleCategoryExpanded = (itemId) => {
    setExpandedCategoryRows((prev) => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  const renderCategoryNames = (item, limit = 10) => {
    const names = item.category_titles?.length ? item.category_titles : item.category_codes;

    if (!Array.isArray(names) || names.length === 0) {
      return "- (Total: 0)";
    }

    const isExpanded = !!expandedCategoryRows[item.id];
    const hasMore = names.length > limit;
    const visible = isExpanded ? names : names.slice(0, limit);

    return (
      <>
        {visible.join(", ")} (Total: {names.length})
        {hasMore && (
          <span
            className="text-primary ml-1"
            role="button"
            onClick={() => toggleCategoryExpanded(item.id)}
          >
            {isExpanded ? "Show less" : "Show more"}
          </span>
        )}
      </>
    );
  };

  const data = keywordsResponse?.data || [];

  return (
    <>
      <ContentBlock>
        <FilterMenu
          totals={totals}
          length={data.length}
          text2="Keywords"
          onChangeCountPage={setPerPage}
          countPage={perPage}
          page={page}
        />
      </ContentBlock>

      <div className="col-12">
        <TabUi tabId={tabId} setTabId={setTabId} />
      </div>

      <ContentBlock containerClass={"py-1"}>
        <TableFilterBox
          filter={true}
          onFilter={onFilter}
          add={false}
          clearFilter={clearFilter}
          button1={
            <div className="float-left ml-2" style={{ width: 170 }}>
              <ButtonSecondary onClick={() => setModalOpt("new_keyword", 1)}>
                New keyword
              </ButtonSecondary>
            </div>
          }
        >
          <div className="d-flex justify-content-between flex-1 flex-wrap">
            <div className="d-flex flex-wrap">
              <div className="px-1">
                <TextInput
                  text="Keyword"
                  name="keyword"
                  style={{ width: 200 }}
                  value={keywordFilter}
                  onChange={(event) => {
                    setKeywordFilter(event.target.value);
                  }}
                />
              </div>
              <div className="px-1">
                <SelectInput
                  label="Active"
                  name="active"
                  style={{ width: 180 }}
                  value={activeFilter}
                  options={[
                    { label: "All", value: "" },
                    { label: "Yes", value: "true" },
                    { label: "No", value: "false" },
                  ]}
                  onChange={(value) => {
                    setActiveFilter(value);
                  }}
                />
              </div>
              <div className="px-1">
                <SelectInput
                  label="Type"
                  name="keyword_match_type"
                  style={{ width: 180 }}
                  value={typeFilter}
                  options={[
                    { label: "All", value: "" },
                    { label: "OR", value: "or" },
                    { label: "AND", value: "and" },
                  ]}
                  onChange={(value) => {
                    setTypeFilter(value);
                  }}
                />
              </div>
              <div className="px-1">
                <SelectInput
                  label="Disable for scope"
                  name="disable_by_scope"
                  style={{ width: 180 }}
                  value={disableByScopeFilter}
                  options={[
                    { label: "All", value: "" },
                    { label: "Yes", value: "true" },
                    { label: "No", value: "false" },
                  ]}
                  onChange={(value) => {
                    setDisableByScopeFilter(value);
                  }}
                />
              </div>
              <div className="px-1">
                <SelectInput
                  label="Ignore if warehouse stock"
                  name="ignore_if_warehouse_stock"
                  style={{ width: 180 }}
                  value={ignoreIfWarehouseStockFilter}
                  options={[
                    { label: "All", value: "" },
                    { label: "Yes", value: "true" },
                    { label: "No", value: "false" },
                  ]}
                  onChange={(value) => {
                    setIgnoreIfWarehouseStockFilter(value);
                  }}
                />
              </div>
              <div className="px-1">
                <SelectInput
                  label="Scope"
                  name="scope"
                  mode="multiple"
                  style={{ width: 220 }}
                  value={scopeFilter}
                  options={SCOPE_OPTIONS}
                  onChange={(value) => {
                    setScopeFilter(value || []);
                  }}
                />
              </div>
              <div className="px-1">
                <TextInput
                  text="Category Title"
                  name="category_title"
                  style={{ width: 180 }}
                  value={categoryTitleFilter}
                  onChange={(event) => {
                    setCategoryTitleFilter(event.target.value);
                  }}
                />
              </div>
              <div className="px-1">
                <TextInput
                  text="Brand Code"
                  name="brand_code"
                  style={{ width: 180 }}
                  value={brandCodeFilter}
                  onChange={(event) => {
                    setBrandCodeFilter(event.target.value);
                  }}
                />
              </div>
            </div>
          </div>
        </TableFilterBox>
        <div className="text-muted ml-2 text-danger">
          <small>
            The rules will only be triggered once the products have been moved. The rules are applied to products that have not been moved every Wednesday at 9:00.
          </small>
        </div>          
        <div className="table-responsive" style={{ overflow: "hidden" }}>
          <ContentValidation loader={isLoading} array={data} error={error}>
            <table className="table mt-3">
              <thead>
                <tr className="text-muted">
                  <th className="text-black">Keywords</th>
                  <th className="text-black">Type</th>
                  <th className="text-black">Disable for scope</th>
                  <th className="text-black">Scopes</th>
                  <th className="text-black">Active</th>
                  <th className="text-black">Ignore if warehouse stock</th>
                  <th className="text-black">Categories</th>
                  <th className="text-black">Brands</th>
                  <th className="text-black"></th>
                </tr>
              </thead>
              <tbody>
                {data.map((item) => (
                  <tr className="align-middle tableColumn" key={item.id}>
                    <td className="align-middle">{(item.keywords || []).join(", ") || "-"}</td>
                    <td className="align-middle">{(item.keyword_match_type || "-").toUpperCase()}</td>
                    <td className="align-middle">{item.disable_by_scope ? "Yes" : "No"}</td>
                    <td className="align-middle">{(item.scopes || []).join(", ") || "-"}</td>
                    <td className="align-middle">{item.active ? "Yes" : "No"}</td>
                    <td className="align-middle">{item.ignore_if_warehouse_stock ? "Yes" : "No"}</td>
                    <td className="align-middle">
                      {renderCategoryNames(item, 10)}
                    </td>
                    <td className="align-middle">{item.brand_codes.join(", ") || "-"}</td>
                    <td className="align-middle">
                      <div
                        className="d-flex justify-content-end"
                        style={{ gap: 5 }}
                      >
                        <ButtonEditRow
                          className="d-flex justify-content-center align-items-center"
                          onClick={() =>
                            setModalOpt("edit_keyword", item.id, item)
                          }
                        />
                        <ButtonHistoryRow
                          className="d-flex justify-content-center align-items-center"
                          onClick={() => setHistoryRecordId(item.id)}
                        />
                        <ButtonDelete
                          className="d-flex justify-content-center align-items-center"
                          onDelete={() =>
                            confirm({
                              title: "Are you sure?",
                              icon: (
                                <ExclamationCircleOutlined className="text-primary" />
                              ),
                              content: "You will not be able to undo this action",
                              onOk: () => {
                                removeMutation.mutate({ id: item.id });
                              },
                            })
                          }
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ContentValidation>
        </div>

        <div className="pagination table-responsive justify-content-center">
          <Pagination
            setPagination={setPage}
            page={page}
            totals={totals}
            pageCount={perPage}
          />
        </div>
      </ContentBlock>

      {["new_keyword", "edit_keyword"].includes(modalOpt.type) && (
        <ModalNonPublishableKeyword
          onClose={closeModal}
          modalOpt={modalOpt}
          onSaved={() => {
            refetch();
            queryClient.invalidateQueries("nonPublishableKeywords");
          }}
        />
      )}

      {historyRecordId && (
        <ModalNonPublishableKeywordHistory
          recordId={historyRecordId}
          onClose={() => setHistoryRecordId(null)}
        />
      )}
    </>
  );
};

export default NonPublishableKeywords;
